import { BaseService } from './BaseService';
import { documentMfaStore } from './documentMfaStore';
import { ILocale } from '../providers/LocaleProvider/types';

interface ISecureDownloadOptions {
  acceptLanguage?: ILocale;
}

const STEP_UP_HEADER = 'X-Document-Download-Token';
const STEP_UP_ERROR_CODE = 'step_up_required';

const resolveAcceptLanguage = (override?: ILocale): string => {
  if (override) return override;
  let locale = localStorage.getItem('locale');
  if (locale) locale = JSON.parse(locale);
  return locale === ILocale.fr ? ILocale.fr : ILocale.en;
};

/**
 * Performs an authenticated GET against a protected document download
 * endpoint, transparently handling step-up MFA. If the server responds with
 * 401 and `code: "step_up_required"`, the cached step-up token is cleared,
 * the MFA modal is displayed, and the request is retried once the user
 * verifies a code.
 */
export const secureDocumentDownload = async (url: string, options: ISecureDownloadOptions = {}): Promise<Blob> => {
  const performFetch = async (): Promise<Response> => {
    const bearer = new BaseService().getAccessTokenHeader();
    const stepUpToken = documentMfaStore.getToken();

    const headers: Record<string, string> = {
      Authorization: bearer,
      'accept-language': resolveAcceptLanguage(options.acceptLanguage),
    };
    if (stepUpToken) {
      headers[STEP_UP_HEADER] = stepUpToken;
    }

    return fetch(url, { method: 'GET', headers });
  };

  let response = await performFetch();

  if (response.status === 401) {
    let body: { code?: string } | null = null;
    try {
      body = await response.clone().json();
    } catch {
      body = null;
    }

    if (body?.code === STEP_UP_ERROR_CODE) {
      documentMfaStore.clearToken();
      await documentMfaStore.requestStepUp();
      response = await performFetch();
    }
  }

  if (!response.ok) {
    throw new Error(`Document download failed with status ${response.status}`);
  }

  return response.blob();
};
